import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAccountId, useIsWebView, useToken, useDoctorRouteGuard } from '../../../shared/hooks';
import { useWebViewLoading } from '../../../shared/hooks/useWebViewLoading';
import { DoctorRouteDialog } from '../../../shared/components/DoctorRouteDialog';
import { appBridge } from '../../../shared/services/bridge';

import {
  NETPROTECTIONS_URL,
  NPAB_SERVICE_BANNER_IMAGE,
  NP_ATOBARAI_TERMS_URL,
} from '../constants/postpaidLinks';
import { RECEIPT_NOTICE_CONTACT_LINES } from '../constants/receiptTexts';
import { usePostpaidPaymentConfirm } from '../hooks/usePostpaidPaymentConfirm';
import PaymentSummarySection from './PaymentSummarySection';
import ReceiptNotice from './ReceiptNotice';

import './PaymentConfirm.css';
import './PaymentCommon.css';

/**
 * NP 後払い 決済確認画面
 *
 * フロー:
 *   /inquiries/:id/payment-method (mode=change & postpaid 選択)
 *     ↓ submit() で payment/set 後にこの画面へ
 *   /inquiries/:id/postpaid-confirm  ← 本画面
 *     ↓ 同意チェック → 「審査へ進む」押下 → createPayment
 *     ↓ API レスポンスの redirect URL に従って遷移
 *   /success?inquiryId=xxx  または  /failed?inquiryId=xxx&postpaidAuth=false
 */
const PostpaidPaymentConfirm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isWebView = useIsWebView();
  const token = useToken();
  const accountId = useAccountId();

  const inquiryId = id ?? '';

  const {
    paymentData,
    amount,
    isLoading,
    error,
    isAgreed,
    isSubmitting,
    email,
    emailError,
    emailUpdateError,
    setEmail,
    onEmailBlur,
    canSubmit,
    toggleAgreed,
    handlePayment,
  } = usePostpaidPaymentConfirm(inquiryId, accountId, token);

  // ドクタールート監視ガード
  const { shouldBlock, handleConfirm: handleGuardConfirm } = useDoctorRouteGuard(id, token);

  const contentRef = useWebViewLoading(isLoading);

  const handleBack = () => navigate(-1);

  const handleChangePaymentMethod = () => {
    if (!id) return;
    navigate(`/inquiries/${id}/payment-method?mode=change`);
  };

  // NP 提供文言中の利用規約リンク・案内バナーのクリックハンドラ。
  // WebView 環境（Flutter アプリ内）: preventDefault + openInAppWebView で HkWebViewScreen に Push
  //   → 外部ブラウザ起動を防ぎ、アプリ内に留めることでユーザー離脱を抑止
  // 非 WebView 環境（ブラウザ直接アクセス）: preventDefault せず、通常の <a target="_blank"> として動作
  //   → ブラウザの別タブで開く（ドクター側と同じ挙動）
  const handleInAppLink = (
    e: React.MouseEvent<HTMLAnchorElement>,
    url: string,
    title: string,
  ) => {
    if (isWebView) {
      e.preventDefault();
      appBridge.openInAppWebView(url, title);
    }
  };

  if (isLoading) {
    return null;
  }

  // ドクタールートに切り替わっていた場合は Flutter ダッシュボードへ強制復帰
  if (shouldBlock) {
    return <DoctorRouteDialog onConfirm={handleGuardConfirm} />;
  }

  if (error || !paymentData || !amount) {
    return (
      <div ref={contentRef} className="confirm-container">
        <main className="confirm-content">
          <p>決済情報の取得に失敗しました。</p>
        </main>
      </div>
    );
  }

  return (
    <div ref={contentRef} className="confirm-container">
      <main className="confirm-content">
        <h2 className="content-title">NP後払い 確認</h2>

        {/* お支払い内容セクション（PPTX指示 0506 スライド12: 共通コンポーネント化） */}
        <PaymentSummarySection
          estimateGroups={paymentData.estimateGroups}
          subtotalExcludingCommission={amount.price}
          commissionAmount={amount.commissionAmount}
          taxAmount={amount.taxAmount}
          totalAmount={amount.totalAmount}
        />

        {/* 領収書発行仕様変更（課題表381 / PDF P7）:
            NP後払いは HomeDoctor の領収書発行対象外のため、問い合わせ先を表示する。 */}
        <ReceiptNotice lines={RECEIPT_NOTICE_CONTACT_LINES} />

        {/* NP 提供文言 (ドクター側 home_doctor_front cb181c9f と統一)。
            「○このお支払方法の詳細」セクション。 */}
        <section className="confirm-section">
          <p className="postpaid-notice-heading">○このお支払方法の詳細</p>
          <p>
            サービス提供後、「コンビニ」「郵便局」「銀行」「PayPay」で後払いできる安心・簡単な決済方法です。請求書は、サービス提供後に郵送されますので、発行から14日以内にお支払いをお願いします。<br />
            お支払い期日を過ぎてもお支払いの確認ができない場合、手数料が加算される場合がございます。<br />
            金額が30万円を超える場合、お支払いは銀行振込のみとなります。
          </p>
        </section>

        {/* NP 提供文言。「○ご注意」セクション + 利用規約リンク 2 本 + 案内バナー。 */}
        <section className="confirm-section">
          <p className="postpaid-notice-heading">○ご注意</p>
          <p>
            後払い手数料：無料<br />
            後払いのご注文には、
            <a
              className="postpaid-link"
              href={NETPROTECTIONS_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) =>
                handleInAppLink(e, NETPROTECTIONS_URL, '株式会社ネットプロテクションズ')
              }
            >
              株式会社ネットプロテクションズ
            </a>
            の後払いサービスが適用され、同社へ代金債権を譲渡します。<br />
            「
            <a
              className="postpaid-link"
              href={NP_ATOBARAI_TERMS_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) =>
                handleInAppLink(e, NP_ATOBARAI_TERMS_URL, 'NP後払い利用規約')
              }
            >
              NP後払い利用規約及び同社のプライバシーポリシー
            </a>
            」に同意して、後払いサービスをご選択ください。詳細はバナーをクリックしてご確認ください。<br />
            ご利用者が未成年の場合、法定代理人の利用同意を得てご利用ください。
          </p>
          {/* NP後払いサービス案内バナー。クリックで利用規約ページへ遷移。 */}
          <a
            className="postpaid-banner"
            href={NP_ATOBARAI_TERMS_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) =>
              handleInAppLink(e, NP_ATOBARAI_TERMS_URL, 'NP後払い利用規約')
            }
          >
            <img
              src={NPAB_SERVICE_BANNER_IMAGE}
              alt="NP後払いサービスのご案内"
              className="postpaid-banner-image"
              width={126}
              height={61}
            />
          </a>
        </section>

        {/* 与信審査に送るメールアドレス。account.email が初期値、未登録なら空欄で必須 */}
        <section className="confirm-section">
          <div className="form-field">
            <label htmlFor="postpaid-email" className="form-label">
              メールアドレス<span className="required-mark">*</span>
            </label>
            <input
              id="postpaid-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={onEmailBlur}
              aria-invalid={!!emailError}
              aria-describedby={emailError ? 'postpaid-email-error' : undefined}
              className="form-input"
            />
            {emailError && (
              <p id="postpaid-email-error" className="form-input-error" role="alert">
                {emailError}
              </p>
            )}
            {/* account 取得失敗 / 更新失敗時のサーバー側エラーをここに集約表示 */}
            {emailUpdateError && (
              <p className="form-input-error" role="alert">
                {emailUpdateError}
              </p>
            )}
          </div>
        </section>

        <div className="agreement-checkbox-container">
          <input
            type="checkbox"
            id="postpaid-agreement"
            checked={isAgreed}
            onChange={toggleAgreed}
          />
          <label htmlFor="postpaid-agreement">NP後払いAirの利用規約に同意します</label>
        </div>
      </main>

      <footer className="confirm-footer">
        <button
          type="button"
          className="button-primary"
          onClick={handlePayment}
          disabled={!canSubmit}
        >
          {isSubmitting ? '処理中...' : '審査へ進む'}
        </button>
        <button
          type="button"
          className="button-secondary"
          onClick={handleChangePaymentMethod}
          disabled={isSubmitting}
        >
          支払方法を変更
        </button>
        {!isWebView && (
          <button className="button-secondary" onClick={handleBack}>
            戻る
          </button>
        )}
      </footer>
    </div>
  );
};

export default PostpaidPaymentConfirm;
